/**
 * Bundle 51 - Profile completion gate.
 *
 * Route: `/rsvp/{registration_id}/complete-profile`
 * Rendered after payment success when the user's profile is missing
 * photos or socials. Blocks ticket issuance until both minimums met.
 *
 * Uses the same media-upload endpoint the multi-step form uses so we
 * are not maintaining a second upload path. Photos are POSTed to
 * `/media/upload` (owner_type=profile), socials are parsed client-side
 * for instant feedback then re-validated by the server on save.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhotoTile } from './ProfileGatePhotoTile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Upload, Loader2, X, Camera,
  Instagram, Twitter, Linkedin, Facebook, Music2,
} from 'lucide-react';
import api from '@/utils/api';

// Bundle 57 - handle-first entry. Matches the profile page pattern
// (see ProfilePage.jsx SOCIAL_FIELDS) so the data users type here
// flows straight into `users.profile.socials` with the same shape.
// The user picks a platform then types just their handle; we
// synthesize the canonical URL on add so the backend
// `social_url_parser` still validates and normalizes.
const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', prefix: '@',
    urlOf: (h) => `https://instagram.com/${h}`,
    icon: Instagram, placeholder: 'yourhandle' },
  { key: 'x', label: 'X', prefix: '@',
    urlOf: (h) => `https://x.com/${h}`,
    icon: Twitter, placeholder: 'yourhandle' },
  { key: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/in/',
    urlOf: (h) => `https://linkedin.com/in/${h}`,
    icon: Linkedin, placeholder: 'your-name' },
  { key: 'tiktok', label: 'TikTok', prefix: '@',
    urlOf: (h) => `https://tiktok.com/@${h}`,
    icon: Music2, placeholder: 'yourhandle' },
  { key: 'facebook', label: 'Facebook', prefix: 'facebook.com/',
    urlOf: (h) => `https://facebook.com/${h}`,
    icon: Facebook, placeholder: 'your.name' },
  { key: 'threads', label: 'Threads', prefix: '@',
    urlOf: (h) => `https://threads.net/@${h}`,
    icon: Music2, placeholder: 'yourhandle' },
];

// Strip common paste-in noise so users can dump a URL or a prefixed
// handle and still get the raw handle back. Mirrors the profile
// page's per-platform `normalize` fns.
const normalizeHandle = (platform, raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const stripped = s
    .replace(/^@/, '')
    .replace(/^https?:\/\/(?:www\.|m\.)?/i, '')
    .replace(/^(?:instagram|facebook|x|twitter|linkedin|tiktok|threads)\.(?:com|net)\//i, '')
    .replace(/^in\//i, platform === 'linkedin' ? '' : 'in/')
    .replace(/^@/, '')
    .replace(/\/.*$/, '')
    .replace(/[.,;:!]+$/, '');
  return stripped;
};

const MAX_MB = 20;

const ProfileCompletionGate = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [socials, setSocials] = useState([]);
  const [platform, setPlatform] = useState('instagram');
  const [handleInput, setHandleInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rsvp/${registrationId}/profile-status`);
        setStatus(data);
        if (!data.gate_required || data.profile_complete) {
          toast.success('Profile already complete. Ticket on its way.');
          navigate('/dashboard');
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not load status');
      }
    })();
  }, [registrationId, navigate]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true); setErr('');
    for (const f of files) {
      if (!/^image\//.test(f.type)) { setErr('Please pick an image file.'); continue; }
      if (f.size > MAX_MB * 1024 * 1024) {
        setErr(`Each picture must be under ${MAX_MB} MB.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('owner_type', 'user_profile');
        fd.append('owner_id', ''); // Server derives from JWT
        fd.append('is_public', 'false');
        const { data } = await api.post('/media/upload/v2', fd);
        setPhotos((prev) => [...prev, { id: data.id, url: data.url, thumb_url: data.thumb_url }]);
      } catch (ex) {
        setErr(ex?.response?.data?.detail || 'Upload failed');
      }
    }
    setUploading(false);
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const activePlatform = useMemo(
    () => PLATFORMS.find((p) => p.key === platform) || PLATFORMS[0],
    [platform],
  );
  const normalizedHandle = useMemo(
    () => normalizeHandle(platform, handleInput),
    [platform, handleInput],
  );

  const addSocial = () => {
    if (!normalizedHandle) {
      setErr('Enter your handle.');
      return;
    }
    if (socials.some((s) => s.platform === platform && s.handle === normalizedHandle.toLowerCase())) {
      setErr('You already added that social.');
      return;
    }
    const url = activePlatform.urlOf(normalizedHandle);
    setSocials((prev) => [...prev, {
      url,
      platform,
      handle: normalizedHandle.toLowerCase(),
      label: activePlatform.label,
      icon: activePlatform.icon,
    }]);
    setHandleInput('');
    setErr('');
  };

  const removeSocial = (idx) => setSocials((prev) => prev.filter((_, i) => i !== idx));

  const totalPhotoCount = (status?.photo_count || 0) + photos.length;
  const totalSocialCount = (status?.social_count || 0) + socials.length;
  const canSave = totalPhotoCount >= (status?.min_photos || 1)
    && totalSocialCount >= (status?.min_socials || 1)
    && !saving && !uploading;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr('');
    try {
      const { data } = await api.post(
        `/rsvp/${registrationId}/complete-profile`,
        {
          photos: photos.map((p) => ({ id: p.id, url: p.url, thumb_url: p.thumb_url })),
          socials: socials.map((s) => ({ url: s.url })),
        },
      );
      toast.success('Profile complete. Your ticket is on the way.');
      setStatus(data);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : d?.message || 'Save failed';
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!status) return (
    <div className="max-w-xl mx-auto py-16 text-center text-neutral-500">
      <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin" />
      Loading your profile...
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="profile-gate">
      <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">
        One more step
      </h1>
      <p className="text-sm text-neutral-700 mb-6 max-w-lg">
        Add at least one photo and one social account so organizers can welcome
        you. Your ticket is issued the moment you save.
      </p>

      {err && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 mb-4 flex items-start gap-2"
             data-testid="profile-gate-error">
          <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {/* Photos */}
      <section className="mb-8" data-testid="profile-gate-photos-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg font-bold text-primary-900">
            Photos
            <span className="ml-2 text-xs font-normal text-neutral-500 font-sans">
              {totalPhotoCount} of up to {status.max_photos}
            </span>
          </h2>
          {totalPhotoCount >= status.min_photos && (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid="profile-gate-photos-ok" />
          )}
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          A clear face helps organizers welcome you. Up to {MAX_MB} MB per picture.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photos.map((p) => (
            <PhotoTile
              key={p.id}
              photo={p}
              onRemove={() => removePhoto(p.id)}
            />
          ))}
          {totalPhotoCount < status.max_photos && (
            <label className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 hover:border-primary-500 hover:bg-primary-50/50 flex flex-col items-center justify-center cursor-pointer text-neutral-500 hover:text-primary-800 transition-colors"
                    data-testid="profile-gate-photo-picker">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onPickFiles}
                disabled={uploading}
                className="hidden"
              />
              {uploading ? <Loader2 className="h-6 w-6 animate-spin" />
                        : <><Camera className="h-6 w-6 mb-1" /><span className="text-xs">Add photo</span></>}
            </label>
          )}
        </div>
      </section>

      {/* Socials */}
      <section className="mb-10" data-testid="profile-gate-socials-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg font-bold text-primary-900">
            Social account
            <span className="ml-2 text-xs font-normal text-neutral-500 font-sans">
              {totalSocialCount} of up to 6
            </span>
          </h2>
          {totalSocialCount >= status.min_socials && (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid="profile-gate-socials-ok" />
          )}
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Pick a platform and enter your handle. Instagram, X,
          LinkedIn, TikTok, Facebook, or Threads.
        </p>

        {socials.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-gate-socials-list">
            {socials.map((s, i) => {
              const Icon = s.icon || Instagram;
              return (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs text-primary-900">
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-semibold">{s.label}</span>
                  <span className="text-neutral-700">/{s.handle}</span>
                  <button type="button" onClick={() => removeSocial(i)} className="ml-1 hover:text-red-700" aria-label="Remove">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            data-testid="profile-gate-social-platform"
            className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-primary-900 focus:outline-none focus:ring-2 focus:ring-secondary-500"
          >
            {PLATFORMS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
          <div className="flex flex-1 rounded-md border border-neutral-200 overflow-hidden focus-within:ring-2 focus-within:ring-secondary-500">
            <span className="inline-flex items-center px-2 text-xs text-neutral-500 bg-neutral-50 border-r border-neutral-200 select-none">
              {activePlatform.prefix}
            </span>
            <Input
              value={handleInput}
              onChange={(e) => setHandleInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSocial();
                }
              }}
              placeholder={activePlatform.placeholder}
              data-testid="profile-gate-social-input"
              className="border-0 focus-visible:ring-0"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={addSocial}
            disabled={!handleInput.trim()}
            data-testid="profile-gate-social-add"
          >
            Add
          </Button>
        </div>

        {handleInput && normalizedHandle && (
          <div className="mt-2 text-xs text-amber-800 inline-flex items-center gap-1.5"
                data-testid="profile-gate-social-detected">
            <span className="font-semibold">Not saved yet.</span>
            <span>
              Press <kbd className="rounded border border-amber-300 bg-white px-1 font-mono">Enter</kbd>
              {' '}or tap <strong>Add</strong> to save
              {' '}{activePlatform.label}/<strong>{normalizedHandle}</strong>.
            </span>
          </div>
        )}
      </section>

      <div className="sticky bottom-0 bg-white border-t border-neutral-200 py-3">
        <Button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="w-full"
          data-testid="profile-gate-save"
        >
          {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                 : <><Upload className="h-4 w-4 mr-1" /> Save and issue ticket</>}
        </Button>
      </div>
    </div>
  );
};

export default ProfileCompletionGate;
